// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

import type { ButtonHTMLAttributes, ReactNode, Ref } from 'react';

import { cx } from './cx';
import styles from './IconButton.module.css';

export type IconButtonVariant = 'filled' | 'ghost' | 'outline';
/** `sm` — 28px square; `md` — 32px square. */
export type IconButtonSize = 'sm' | 'md';
export type IconButtonShape = 'square' | 'circle';

export interface IconButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** The accessible name (→ `aria-label`), and the tooltip unless `title` is given. */
  label: string;
  /** `filled` (default): a tinted well. `ghost`: transparent until hovered. `outline`: the legacy bordered look. */
  variant?: IconButtonVariant;
  /** Default `md`. */
  size?: IconButtonSize;
  /** Default `square`. */
  shape?: IconButtonShape;
  /** The icon (an svg, sized to `--space-4`). */
  children: ReactNode;
  ref?: Ref<HTMLButtonElement>;
}

/**
 * A button that is only an icon. `type` defaults to `"button"`; `className`
 * lands on the `<button>`. `aria-pressed` gives the on-state (TopBar gear).
 */
export function IconButton({
  label,
  variant = 'filled',
  size = 'md',
  shape = 'square',
  title,
  className,
  type = 'button',
  children,
  ...rest
}: IconButtonProps) {
  return (
    <button
      type={type}
      aria-label={label}
      title={title ?? label}
      className={cx(styles.button, styles[variant], styles[size], styles[shape], className)}
      {...rest}
    >
      {children}
    </button>
  );
}

/** The one close ×: a 16px stroked cross, so every close reads the same. */
export function XIcon() {
  return (
    <svg
      className={styles.x}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M4 4l8 8M12 4l-8 8" />
    </svg>
  );
}

/** Edit: a 16px stroked pencil, drawn like XIcon. */
export function PencilIcon() {
  return (
    <svg
      className={styles.x}
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M11 2.5l2.5 2.5L5.5 13H3v-2.5L11 2.5zM9.5 4L12 6.5" />
    </svg>
  );
}
